/** Work / Genetics and mating: what needs a look first, then pedigrees, EPDs, DNA, mating plans, groups and traits. */
import type { ReactNode } from "react";
import { Link, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import type { AnimalDetail } from "../../../contract";
import { openRecord } from "../../../web/forms/EventForm";
import { RefPicker } from "../../../web/forms/pickers";
import { formatDate } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { useCaptureSubject } from "../../../web/shell/Composer";
import { Page } from "../../../web/shell/Layout";
import { Button, Card, Chip, Empty, ErrorNote, Field, Icon, List, Loading, PageHeader, Row, Section, Stat, Tabs } from "../../../web/ui/kit";
import { ISSUE_LABELS, WORKSPACE_SECTION_LABELS } from "../shared/labels";
import type { OverviewDTO, PedigreeIssue } from "../shared/types";
import { ContemporaryPage } from "./Contemporary";
import { DnaPage } from "./DnaPage";
import { EpdPage } from "./EpdCompare";
import { MatingList, NewMatingPlan } from "./Mating";
import { PedigreeView } from "./PedigreeTree";
import { TraitsPage } from "./Traits";

const SECTIONS = ["", "pedigree", "epds", "dna", "mating", "groups", "traits"];

function SectionTabs() {
  const loc = useLocation();
  const current = loc.pathname.replace(/^\/work\/genetics\/?/, "").split("/")[0];
  return <Tabs value={current} tabs={SECTIONS.map((id) => ({ id, label: t(WORKSPACE_SECTION_LABELS[id || "overview"]), to: `/work/genetics${id ? `/${id}` : ""}` }))} />;
}

export function GeneticsShell({ title, subtitle, actions, children }: { title: string; subtitle?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <Page>
      <PageHeader back={{ to: "/work", label: t("Work") }} eyebrow={t("Genetics and mating")} title={title} subtitle={subtitle} actions={actions} />
      <SectionTabs />
      {children}
    </Page>
  );
}

function Overview() {
  useLanguage();
  const can = useCan();
  const o = useApi<OverviewDTO>("/m/genetics/overview");
  const issues = useApi<{ issues: PedigreeIssue[] }>("/m/genetics/pedigree-review");
  return (
    <GeneticsShell title={t("Genetics and mating")} subtitle={t("Pedigrees, EPDs, DNA and mating plans in one place.")}
      actions={can("record") && <Button variant="primary" icon="plus" to="/work/genetics/mating/new">{t("Start a mating plan")}</Button>}>
      {o.isLoading ? <Loading /> : !o.data ? <ErrorNote error={o.error} retry={() => o.refetch()} /> : (
        <>
          <div className="row-actions">
            <Stat label={t("Pedigrees to fix")} value={o.data.pedigree_issues} tone={o.data.pedigree_issues ? "warn" : undefined} to="/work/genetics/pedigree" />
            <Stat label={t("DNA at the lab")} value={o.data.dna.sent} to="/work/genetics/dna" />
            <Stat label={t("Animals with EPDs")} value={o.data.evaluations.animals} hint={o.data.evaluations.latest ? t("latest {{date}}", { date: formatDate(o.data.evaluations.latest) }) : undefined} to="/work/genetics/epds" />
            <Stat label={t("Open mating plans")} value={o.data.mating.active} to="/work/genetics/mating" />
            <Stat label={t("Straws on hand")} value={o.data.inventory.semen_straws} to="/ranch/genetic-inventory" />
          </div>
          {(issues.data?.issues.length ?? 0) > 0 && (
            <Section title={t("Needs a look")} count={issues.data!.issues.filter((i) => i.kind !== "suspected").length || undefined}>
              <IssueList issues={issues.data!.issues.slice(0, 6)} />
              {issues.data!.issues.length > 6 && <Button variant="ghost" to="/work/genetics/pedigree">{t("All pedigree issues")}</Button>}
            </Section>
          )}
          <Section title={t("Do something")}>
            <div className="cards">
              <Card to="/work/genetics/epds"><Icon name="report" /><p className="job-title">{t("Compare EPDs")}</p><p className="muted">{t("Side by side, with percentile ranks and run dates.")}</p></Card>
              <Card to="/work/genetics/dna"><Icon name="dna" /><p className="job-title">{t("DNA samples and results")}</p><p className="muted">{plural(o.data.dna.collected, "{{count}} pulled, not sent", "{{count}} pulled, not sent")} · {plural(o.data.dna.resulted, "{{count}} with results", "{{count}} with results")}</p></Card>
              <Card to="/work/registration"><Icon name="award" /><p className="job-title">{t("Register calves")}</p><p className="muted">{o.data.registration.rejected ? plural(o.data.registration.rejected, "{{count}} rejection to fix", "{{count}} rejections to fix") : plural(o.data.registration.open, "{{count}} open batch", "{{count}} open batches")}</p></Card>
              <Card to="/work/genetics/groups"><Icon name="group" /><p className="job-title">{t("Contemporary groups")}</p><p className="muted">{t("Group calves that had the same chance.")}</p></Card>
              <Card to="/work/genetics/traits"><Icon name="ruler" /><p className="job-title">{t("Trait definitions")}</p><p className="muted">{t("Scores, units and codes your records use.")}</p></Card>
            </div>
          </Section>
        </>
      )}
    </GeneticsShell>
  );
}

export function IssueList({ issues }: { issues: PedigreeIssue[] }) {
  const can = useCan();
  const fix = (i: PedigreeIssue) => {
    if (i.kind === "failed_sample") return openRecord({ initial: { type: "genetics.dna_sample", subjects: [{ kind: "animal", id: i.animal.id, role: "animal", label: i.animal.label }], data: { sample_type: "hair" } } });
    openRecord({ initial: { type: "genetics.pedigree_correction", subjects: [{ kind: "animal", id: i.animal.id, role: "animal", label: i.animal.label }],
      data: { role: i.role ?? "sire", ...(i.kind === "confirmed_differs" && i.asserted ? { parent_id: i.asserted.id } : {}), ...(i.kind === "suspected" && i.asserted ? { parent_id: i.asserted.id } : {}), basis: i.basis === "dna" || i.kind === "excluded_linked" ? "dna" : i.basis ?? "recorded" } } });
  };
  return (
    <List>
      {issues.map((i) => {
        const detail = i.kind === "excluded_linked" ? (i.linked ? t("{{parent}} is on record but was excluded", { parent: i.linked.label }) : t("The lab excluded the recorded parent"))
          : i.kind === "confirmed_differs" ? t("Confirmed {{asserted}}, record says {{linked}}", { asserted: i.asserted?.label ?? "", linked: i.linked?.label ?? t("unknown") })
          : i.kind === "suspected" ? (i.asserted ? t("Likely {{parent}}, not confirmed", { parent: i.asserted.label }) : t("More than one possible sire"))
          : t("Collect a new sample");
        return (
          <Row key={`${i.animal.id}-${i.kind}-${i.role ?? ""}`} icon={i.kind === "failed_sample" ? "dna" : "pedigree"} tone={i.kind === "excluded_linked" ? "bad" : i.kind === "suspected" ? "neutral" : "warn"}
            title={<><Link to={`/work/genetics/pedigree/${i.animal.id}`}>{i.animal.label}</Link> · {t(ISSUE_LABELS[i.kind])}</>}
            meta={[detail, i.on ? formatDate(i.on) : null].filter(Boolean).join(" · ")}
            trailing={can("record") ? <Button size="sm" variant={i.kind === "excluded_linked" ? "primary" : "secondary"} onClick={() => fix(i)}>{i.kind === "failed_sample" ? t("Record sample") : t("Fix")}</Button> : undefined} />
        );
      })}
    </List>
  );
}

function PedigreeReview() {
  useLanguage();
  const navigate = useNavigate();
  const q = useApi<{ issues: PedigreeIssue[] }>("/m/genetics/pedigree-review");
  return (
    <GeneticsShell title={t("Pedigrees")} subtitle={t("Find an animal's family tree, and fix parents the evidence disagrees with.")}>
      <Field label={t("Show a pedigree")}><RefPicker target="animal" value={null} onChange={(v) => v && navigate(`/work/genetics/pedigree/${v}`)} filter={{ status: "all" }} placeholder={t("Tag, name or registration")} /></Field>
      <Section title={t("Needs a look")} count={q.data?.issues.length}>
        {q.isLoading ? <Loading /> : !q.data?.issues.length ? <Empty icon="pedigree" title={t("No pedigree problems")} body={t("DNA results and association records agree with what's on file.")} /> : <IssueList issues={q.data.issues} />}
      </Section>
    </GeneticsShell>
  );
}

function PedigreePage() {
  useLanguage();
  const { id } = useParams();
  const can = useCan();
  const a = useApi<AnimalDetail>(`/animals/${id}`);
  useCaptureSubject(a.data ? { subject: { kind: "animal", id: a.data.id, label: a.data.label } } : null);
  if (a.isLoading) return <Loading />;
  if (!a.data) return <ErrorNote error={a.error} />;
  const subj = [{ kind: "animal" as const, id: a.data.id, role: "animal", label: a.data.label }];
  return (
    <Page>
      <PageHeader back={{ to: "/work/genetics/pedigree", label: t("Pedigrees") }} eyebrow={t("Pedigree")} title={<Link to={`/cattle/${a.data.id}`}>{a.data.label}</Link>}
        subtitle={[a.data.breed, a.data.birth_date ? formatDate(a.data.birth_date) : null, a.data.status === "reference" ? t("Reference only") : null].filter(Boolean).join(" · ")}
        actions={can("record") && <>
          <Button icon="edit" onClick={() => openRecord({ initial: { type: "genetics.pedigree_correction", subjects: subj, data: { role: "sire" } } })}>{t("Correct pedigree")}</Button>
          <Button icon="pedigree" variant="ghost" onClick={() => openRecord({ initial: { type: "genetics.parentage", subjects: subj, data: { role: "sire", basis: "dna" } } })}>{t("Record parentage")}</Button>
        </>} />
      <PedigreeView animalId={a.data.id} defaultDepth={3} />
      <div className="row-actions"><a className="btn btn-ghost btn-sm" href={`/api/m/genetics/pedigree/${a.data.id}/export?depth=5`} download><Icon name="download" size={18} />{t("Download 5 generations")}</a></div>
      {a.data.facets.genetics ? <Chip tone="genetics" icon="info">{t("Verified marks come from DNA results and association records.")}</Chip> : null}
    </Page>
  );
}

export function GeneticsWorkspace() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="pedigree" element={<PedigreeReview />} />
      <Route path="pedigree/:id" element={<PedigreePage />} />
      <Route path="epds" element={<EpdPage />} />
      <Route path="dna" element={<DnaPage />} />
      <Route path="mating" element={<MatingList />} />
      <Route path="mating/new" element={<NewMatingPlan />} />
      <Route path="groups" element={<ContemporaryPage />} />
      <Route path="traits" element={<TraitsPage />} />
      <Route path="*" element={<Page><Empty icon="help" title={t("We couldn't find that.")} action={<Button to="/work/genetics">{t("Back to Genetics and mating")}</Button>} /></Page>} />
    </Routes>
  );
}
